/*!
 * fxBus.js — fx(): one room, one echo, one filter, shared by several sounds
 * © 2026 Rob Canning — GPLv3
 *
 *     fx(room, reverb:{mix:0.5, time:4})          a bus called room, with a reverb
 *     fx(room, [a1, a2, a3])                      these sounds go through it
 *     fx(room, a1, a2, reverb:{mix:0.4}, delay:{time:0.375, fb:0.4, mix:0.2})
 *     say(a1, bus:room) / synth(..., bus:room)     ... or a sound says so itself
 *     fx(room, reverb:{mix:follow(beat.phase, 0.2, 0.8)}, amp:0.8)
 *     fx(room, filter:{type:lp, freq:follow(bass.low, 300, 6000)})
 *     level(room, 0, 8)                           the whole bus, faded
 *     fx(room, reverb:{mix:0.4})                  run again without the delay: the delay comes out
 *     fx(room, delay:off)                         ... or by name
 *     fx(room, off)                               gone: its sounds go out as before
 *     fx(dirt, fuzz:{starve:0.2, gain:40}, sub:1)  a fuzz and a sub on the bus: one pedal for all it carries
 *
 * A sound's own reverb:, delay:, filter: stay its own; a bus is where it
 * goes after them, together with others -- three voices in one room, not
 * three rooms. Sending is by name: every voice of the name, sounding now or
 * to come (each new sentence a text says, each new note), goes through the
 * bus (level.js routeName). The bus is a name too: its level is level(room, ...).
 *
 * Settings run again change it in place: the mix and the filter glide, a new
 * room crossfades while the old rings out, a new delay takes over while the
 * old one's echoes die. Times are beats (reverb time, pre; delay time).
 * The effects are synth()'s and audio()'s: reverb.js, the same delay.
 *
 *     fx(dirt, fuzz:{starve:0.2, gain:40}, sub:{level:1}, drive:0.5, reverb:{mix:0.3})
 *
 * ... and a voice's sub:, drive: and fuzz: (sub.js, fuzz.js), first in the
 * bus in that order, then filter, delay, reverb: one fuzz for every sound
 * sent, not one a voice -- cheaper, and another sound: it hears the notes
 * after their envelopes (it squashes them, as a pedal does) and all of
 * them at once (a chord's notes grind against each other); the sub finds
 * the strongest pitch. A fuzz comes out as loud whatever goes in: fuzz:{level:}
 * (0.25, as loud as a voice can be) is how loud, after it.
 *
 * A bus is as wide as the speakers (speakers.js): on speakers(quad) its
 * effects run on four channels, a sound placed with azi: keeping its place
 * through them -- the reverb a room for each pair of outputs. A change of
 * speakers builds its effects again at the new width.
 */

import { sharedAudioCtx, masterOutput, evalMaybeRandom } from "./audioShared.js";
import { channelFor, routeName, sendName, unsendNode } from "./level.js";
import { buildReverb, updateReverb, disconnectReverb } from "./reverb.js";
import { buildFuzz } from "./fuzz.js";
import { buildSub } from "./sub.js";
import { buildGrit, GRIT_KEYS } from "./grit.js";
import { feedbackLoop, FB_MAX } from "./feedback.js";
import { driveCurve } from "./synthTimbre.js";
import { isBindable, bindParam, releaseBinding } from "../../control/paramBinding.js";
import { secsOf, secPerBeat } from "../beats.js";
import { isRel, applyRel } from "../relative.js";
import { onReset } from "../../system/teardown.js";
import { widthInput, outputWidth, onWidth } from "./speakers.js";

/** What a setting is before any is given: for mix:*1.5 and the like. Times in beats. */
const FX_DEFAULTS = {
  reverb: { mix: 0.2, time: 1.8, damp: 1800, pre: 0.015 },
  delay: { mix: 0.25, fb: 0.3, time: 0.25 },
  filter: { freq: 1200, q: 0.707 },
  fuzz: { starve: 0.5, gain: 30, bias: 0.3, tone: 2500, level: 0.25 },
  sub: { level: 0.8, octave: 1, tone: 0.5 },
};
const BEAT_KEYS = new Set(["time", "pre", "predelay"]);
const GROUPS = ["reverb", "delay", "filter", "fuzz", "sub", "drive", ...GRIT_KEYS];
const FUZZ_LEVEL = 0.25;            // a bus's fuzz, after it: as loud as a voice can be (synth amp: at most 0.25)
/** sub:, fuzz: as written: a number or an object, rand()s drawn. */
const rolled = v => (v && typeof v === "object" && !Array.isArray(v) && v.type == null
  ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, evalMaybeRandom(x)])) : evalMaybeRandom(v));

/** mix:*1.5, freq:*0.5, amp:+=0.1 -- from the bus's settings now (kept in seconds: times back to beats). */
function relativeToBus(name, args) {
  const bus = buses.get(name);
  const out = { ...args };
  for (const grp of ["reverb", "delay", "filter", "fuzz", "sub"]) {
    const o = out[grp];
    if (!o || typeof o !== "object") continue;
    const now = bus?.settings?.[grp] || {};
    out[grp] = Object.fromEntries(Object.entries(o).map(([k, v]) => {
      if (!isRel(v)) return [k, v];
      let base = Number(now[k]);
      if (Number.isFinite(base) && BEAT_KEYS.has(k)) base /= secPerBeat();
      return [k, applyRel(Number.isFinite(base) ? base : FX_DEFAULTS[grp][k], v)];
    }));
  }
  if (isRel(out.amp)) out.amp = applyRel(bus?.out.gain.value ?? 1, out.amp);
  if (isRel(out.drive)) out.drive = applyRel(Number(bus?.settings?.drive) || 0, out.drive);
  return out;
}

const buses = new Map();            // name -> bus
const members = new Map();          // sound name -> bus name
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const num = (v, d) => { const n = Number(evalMaybeRandom(v)); return Number.isFinite(n) ? n : d; };
const FILTER_TYPES = { lp: "lowpass", hp: "highpass", bp: "bandpass", notch: "notch" };

/** A bus's effects settings, times in seconds, follow()s set aside as `binds`. */
function settingsOf(args) {
  const binds = [];
  const take = (obj, key, path, fallback) => {
    const v = obj?.[key];
    if (isBindable(v)) { binds.push({ path, value: v }); return fallback; }
    return v;
  };
  const out = {};
  if (args.reverb && typeof args.reverb === "object") {
    const r = secsOf({ ...args.reverb }, ["time", "pre", "predelay"]);
    r.mix = take(r, "mix", "reverb.mix", FX_DEFAULTS.reverb.mix);
    out.reverb = r;
  }
  if (args.delay && typeof args.delay === "object") {
    const d = secsOf({ ...args.delay }, ["time"]);
    d.mix = take(d, "mix", "delay.mix", 0.25);
    d.fb = take(d, "fb", "delay.fb", 0.3);
    out.delay = d;
  }
  if (args.filter && typeof args.filter === "object") {
    const f = { ...args.filter };
    f.freq = take(f, "freq", "filter.freq", 1200);
    f.q = take(f, "q", "filter.q", 0.707);
    out.filter = f;
  }
  for (const g of ["fuzz", "sub", "drive"]) if (args[g] != null) out[g] = rolled(args[g]);
  // fold: clip: ring: comb: crush: as written -- a follow() in one is grit.js's to bind
  for (const g of GRIT_KEYS) if (args[g] != null) out[g] = isBindable(args[g]) ? args[g] : rolled(args[g]);
  if (args.amp != null) out.amp = isBindable(args.amp) ? (binds.push({ path: "amp", value: args.amp }), 1) : num(args.amp, 1);
  return { settings: out, binds };
}

/** The effects between a bus's input and its output: sub, drive, fuzz, grit, filter, delay, reverb -- w channels wide. */
function buildMiddle(ctx, s, input, out, w = outputWidth()) {
  const nodes = [], stops = [];
  let after = input;
  let filter = null, delay = null, reverb = null, sub = null, shaper = null, fuzz = null;
  // an octave pedal, an overdrive, a fuzz -- as in a voice, in that order
  if (s.sub != null) {
    sub = buildSub(ctx, s.sub, w);
    after.connect(sub.input);
    after = sub.output;
    stops.push(sub);
  }
  if (driveCurve(s.drive)) {
    shaper = ctx.createWaveShaper();
    shaper.curve = driveCurve(s.drive);
    shaper.oversample = "4x";
    after.connect(shaper);
    after = shaper;
    nodes.push(shaper);
  }
  if (s.fuzz != null) {
    // into it as a voice's wave goes in (up to full scale), as the grit
    // below: a bus carries its sounds at their level, and a starved fuzz's
    // dead zone gated them all to silence
    const f = buildFuzz(ctx, s.fuzz, w);
    const up = ctx.createGain(), level = ctx.createGain();
    up.gain.value = 1 / FUZZ_LEVEL;
    level.gain.value = fuzzLevel(s.fuzz);
    after.connect(up).connect(f.input);
    f.output.connect(level);
    after = level;
    fuzz = { ...f, level };
    stops.push(f);
    nodes.push(up, level);
  }
  // fold: clip: ring: comb: crush: -- after the fuzz, as in a voice (grit.js)
  // A bus carries its sounds at their level (a voice at most 0.25): up to
  // full scale into it, as a voice's wave goes in, and back down after --
  // two bits of a quiet sound are silence, a fold of one barely folds
  const grit = buildGrit(ctx, s, w);
  if (grit) {
    const up = ctx.createGain(), down = ctx.createGain();
    up.gain.value = 1 / FUZZ_LEVEL;
    down.gain.value = FUZZ_LEVEL;
    after.connect(up).connect(grit.input);
    grit.output.connect(down);
    after = down;
    nodes.push(up, down);
    const unbind = grit.bind();
    stops.push({ stop: () => { for (const u of unbind) u(); grit.stop(); } });
  }
  if (s.filter) {
    filter = ctx.createBiquadFilter();
    filter.type = FILTER_TYPES[String(s.filter.type ?? "lp").toLowerCase()] || String(s.filter.type);
    filter.frequency.value = clamp(num(s.filter.freq, 1200), 20, 20000);
    filter.Q.value = clamp(num(s.filter.q, 0.707), 0.0001, 30);
    after.connect(filter);
    after = filter;
    nodes.push(filter);
  }
  if (s.delay) {
    const d = s.delay;
    const mix = clamp(num(d.mix, 0.25), 0, 1);
    delay = { dry: ctx.createGain(), wet: ctx.createGain(), sum: ctx.createGain(), line: ctx.createDelay(5) };
    delay.dry.gain.value = 1 - mix;
    delay.wet.gain.value = mix;
    delay.line.delayTime.value = clamp(num(d.time, 0.25), 0, 5);
    // through a tone and a saturator, as a voice's (feedback.js): fb up to 1.5
    const loop = feedbackLoop(ctx, delay.line, { fb: clamp(num(d.fb, 0.3), 0, FB_MAX), tone: d.tone, sat: d.sat });
    Object.assign(delay, { fb: loop.fbGain, tone: loop.tone, sat: loop.shaper });
    delay.loop = loop;
    after.connect(delay.dry).connect(delay.sum);
    after.connect(delay.line);
    delay.line.connect(delay.wet).connect(delay.sum);
    after = delay.sum;
    nodes.push(...Object.values(delay));
  }
  if (s.reverb) {
    reverb = buildReverb(ctx, s.reverb, w);
    after.connect(reverb.dry);
    after.connect(reverb.input);
    reverb.dry.connect(out);
    reverb.wet.connect(out);
    stops.push({ stop: () => disconnectReverb(reverb) });   // its room as it is then (a new time swaps it)
  } else after.connect(out);
  return { filter, delay, reverb, sub, shaper, fuzz, grit, nodes, stops, w, tail: (reverb?.time ?? 0) + (delay ? 8 * delay.line.delayTime.value : 0) };
}

/** fuzz:{level:} -- how loud the bus's fuzz comes out (it comes out as loud whatever goes in). */
// fuzz:0.7 has no level: the default (num() reads null as 0, silence)
const fuzzLevel = f => clamp(num(f && typeof f === "object" ? f.level : undefined, FUZZ_LEVEL), 0, 2);

/** Whether new settings change what is wired (else they are set in place). */
const followsIn = v => isBindable(v) || (v && typeof v === "object" && Object.values(v).some(isBindable));
const shapeOf = s => [!!s.filter, s.filter?.type, !!s.delay, !!s.reverb, s.sub != null, !!driveCurve(s.drive), s.fuzz != null,
  // grit: there or not, and a follow() in it (bound when it is built)
  GRIT_KEYS.some(k => s[k] != null), GRIT_KEYS.some(k => followsIn(s[k]))].join("|");

/** The bus `name`, made (with no effects) if it is not there. */
export function fxBus(name) {
  let bus = buses.get(name);
  if (bus) return bus;
  const ctx = sharedAudioCtx;
  // as wide as the speakers (speakers.js): a sound placed by azi: keeps
  // its place through a filter or a delay; a reverb here is two channels
  const input = widthInput(ctx);
  const out = ctx.createGain();
  // the bus is a name: level(room, ...) is its fader
  out.connect(channelFor(name, masterOutput));
  bus = { name, input, out, settings: {}, middle: buildMiddle(ctx, {}, input, out) };
  buses.set(name, bus);
  return bus;
}

/**
 * Change a bus's effects: in place where the wiring stays, else rewired with
 * the old tails ringing out. `drop`: groups taken out (reverb, delay, filter).
 */
function setEffects(bus, s, drop = []) {
  const ctx = sharedAudioCtx, now = ctx.currentTime;
  const merged = {
    filter: s.filter ? { ...bus.settings.filter, ...s.filter } : bus.settings.filter,
    delay: s.delay ? { ...bus.settings.delay, ...s.delay } : bus.settings.delay,
    reverb: s.reverb ? { ...bus.settings.reverb, ...s.reverb } : bus.settings.reverb,
    // sub:, fuzz: -- as written (fuzz:0.7 or an object), merged when both are objects
    sub: mergeOne(bus.settings.sub, s.sub),
    fuzz: mergeOne(bus.settings.fuzz, s.fuzz),
    drive: s.drive ?? bus.settings.drive,
    ...Object.fromEntries(GRIT_KEYS.map(k => [k, mergeOne(bus.settings[k], s[k])])),
  };
  for (const g of drop) merged[g] = undefined;
  if (s.amp != null) bus.out.gain.setTargetAtTime(clamp(num(s.amp, 1), 0, 2), now, 0.02);   // as a followed amp: at most 2
  const m = bus.middle;
  if (shapeOf(merged) === shapeOf(bus.settings)) {
    if (s.filter && m.filter) {
      if (s.filter.freq != null) m.filter.frequency.setTargetAtTime(clamp(num(merged.filter.freq, 1200), 20, 20000), now, 0.02);
      if (s.filter.q != null) m.filter.Q.setTargetAtTime(clamp(num(merged.filter.q, 0.707), 0.0001, 30), now, 0.02);
    }
    if (s.delay && m.delay) {
      const mix = clamp(num(merged.delay.mix, 0.25), 0, 1);
      m.delay.dry.gain.setTargetAtTime(1 - mix, now, 0.02);
      m.delay.wet.gain.setTargetAtTime(mix, now, 0.02);
      m.delay.line.delayTime.setTargetAtTime(clamp(num(merged.delay.time, 0.25), 0, 5), now, 0.05);
      m.delay.fb.gain.setTargetAtTime(clamp(num(merged.delay.fb, 0.3), 0, FB_MAX), now, 0.02);
      if (s.delay.tone != null || s.delay.sat != null) m.delay.loop.set({ tone: merged.delay.tone, sat: merged.delay.sat }, now);
    }
    if (s.reverb && m.reverb) updateReverb(m.reverb, s.reverb);
    if (s.sub != null && m.sub) m.sub.set(merged.sub);
    if (s.fuzz != null && m.fuzz) { m.fuzz.set(merged.fuzz); m.fuzz.level.gain.setTargetAtTime(fuzzLevel(merged.fuzz), now, 0.02); }
    if (s.drive != null && m.shaper) m.shaper.curve = driveCurve(merged.drive);
    if (m.grit && GRIT_KEYS.some(k => s[k] != null)) m.grit.set(merged);
  } else rewire(bus, merged);
  bus.settings = merged;
}

/** The input to new effects; the old ones ring out what they heard, then go. */
function rewire(bus, settings) {
  const m = bus.middle;
  try { bus.input.disconnect(); } catch { /* not connected */ }
  bus.middle = buildMiddle(sharedAudioCtx, settings, bus.input, bus.out);
  setTimeout(() => {
    for (const n of m.nodes) { try { n.disconnect(); } catch { /* gone */ } }
    for (const fx of m.stops || []) fx.stop();
  }, (m.tail + 0.5) * 1000);
}

// new speakers: every bus's effects again, as wide as the chain now is
onWidth(w => { for (const bus of buses.values()) if (bus.middle.w !== w) rewire(bus, bus.settings); });

/** One of sub:, fuzz: run again: its keys on top of the ones it had (a number, as it is). */
function mergeOne(was, now) {
  if (now == null) return was;
  return was && typeof was === "object" && typeof now === "object" ? { ...was, ...now } : now;
}

/** A follow() on a bus setting: moves it as the signal does. */
function bindSetting(bus, { path, value }) {
  const owner = `fx:${bus.name}:${path}`;
  const [group, key] = path.split(".");
  const range = { "reverb.mix": [0, 1], "delay.mix": [0, 1], "delay.fb": [0, FB_MAX], "filter.freq": [20, 20000], "filter.q": [0.0001, 30], amp: [0, 2] }[path];
  bindParam(value, v => setEffects(bus, key ? { [group]: { [key]: v } } : { amp: v }), { owner, min: range[0], max: range[1], default: range[0] });
}

/** Send the sound `name` through the bus `busName` (null or "none": its own way again). */
export function sendTo(name, busName) {
  name = String(name ?? "").split("__")[0];          // a voice of the name: the name
  if (!name) return;
  if (busName == null || ["none", "off", "0"].includes(String(busName))) {
    members.delete(name);
    routeName(name, null);
    return;
  }
  const bus = fxBus(String(busName));
  members.set(name, bus.name);
  routeName(name, bus.input, bus.name);
}

/**
 * send:{bus:room, amount:0.5}: a copy of every voice of `name` into the bus,
 * the sound itself still going its own way; none / off takes its sends
 * away. The send's gain (level.js sendName), or null.
 */
export function sendCopy(name, busName, amount = 1) {
  name = String(name ?? "").split("__")[0];
  if (!name) return null;
  if (busName == null || ["none", "off", "0"].includes(String(busName))) return sendName(name, null);
  return sendName(name, fxBus(String(busName)).input, amount);
}

/** Take the bus away: its sounds go out their own way, its tails ring out. */
function removeBus(name) {
  const bus = buses.get(name);
  if (!bus) return;
  for (const [snd, b] of [...members]) if (b === name) sendTo(snd, null);
  unsendNode(bus.input);
  for (const path of ["reverb.mix", "delay.mix", "delay.fb", "filter.freq", "filter.q", "amp"]) releaseBinding(`fx:${name}:${path}`);
  buses.delete(name);
  setTimeout(() => {
    for (const n of [bus.input, ...bus.middle.nodes, bus.out]) { try { n.disconnect(); } catch { /* gone */ } }
    for (const fx of bus.middle.stops || []) fx.stop();
  }, (bus.middle.tail + 0.5) * 1000);
}
// a project's buses go with it: a sound of the same name in the next one
// would be sent into the old bus, effects and all
onReset("project", function removeAllBuses() { for (const name of [...buses.keys()]) removeBus(name); });

/** The buses and who is in them (for the console and tests). */
export function fxBuses() {
  return [...buses.keys()].map(name => ({ name, members: [...members].filter(([, b]) => b === name).map(([s]) => s), settings: buses.get(name).settings }));
}

/** fx(...) as a cue: fx(room, [a1, a2], reverb:{...}) / fx(room, off). */
export function handleFxCue(ast) {
  const args = {};
  let names = [];
  for (const a of ast?.args || []) {
    if (a.type === "names") names = a.value.map(String);
    else args[a.type] = a.value;
  }
  const name = String(args.uid ?? names.shift() ?? "");
  if (!name) { console.warn("[fx] which bus? fx(room, reverb:{mix:0.4}) -- then fx(room, [a1, a2])"); return; }
  if (names.includes("off") || args.off != null || args.remove != null) { removeBus(name); return; }
  // a sound's own key on a bus line (wave:, env: ...) does nothing here: said, not lost silently
  const stray = Object.keys(args).filter(k => !["uid", "amp", ...GROUPS].includes(k));
  if (stray.length) console.warn(`[fx] ${stray.map(k => k + ":").join(", ")} not a bus's -- it has sub:, drive:, fuzz:, fold:, clip:, ring:, comb:, crush:, filter:, delay:, reverb:, amp: -- ${stray.length > 1 ? "they go" : "it goes"} on the synth line`);
  // The line is the bus's effects: one it no longer names comes out (a
  // delay taken off the line and run again: no more echoes) -- when it
  // names any, and not as a tweak (mix:*1.5: the rest stay). A line only
  // sending sounds (fx(room, [a1, a2])) leaves them as they are. delay:off
  // takes one out by name.
  const isOff = v => v === 0 || /^(off|none|0)$/i.test(String(v));
  const given = GROUPS.filter(g => args[g] != null && !isOff(args[g]));
  const tweak = given.length > 0 && given.every(g => isRel(args[g]) || (typeof args[g] === "object"
    && Object.values(args[g]).length > 0 && Object.values(args[g]).every(isRel)));
  const drop = GROUPS.filter(g => (args[g] != null ? isOff(args[g])
    : given.length > 0 && !tweak && !!buses.get(name)?.settings?.[g]));
  for (const g of drop) delete args[g];
  const rel = relativeToBus(name, args);
  const bus = fxBus(name);
  const { settings, binds } = settingsOf(rel);
  for (const g of drop) for (const k of ["mix", "fb", "freq", "q"]) releaseBinding(`fx:${name}:${g}.${k}`);
  if (Object.keys(settings).length || drop.length) setEffects(bus, settings, drop);
  for (const b of binds) bindSetting(bus, b);
  for (const n of names) sendTo(n, name);
}
